import { getSelectedExerciseId } from './alternateExercise';
/**
 * Get every unique exercise id in a workout, including from supersets
 */
export function getWorkoutExerciseIds(workout) {
    const exerciseIds = workout.sections.flatMap((s) => s.entries.flatMap((entry) => {
        if (entry.__type === 'WorkoutExercise') {
            return entry.libraryExerciseId;
        }
        return entry.exercises.map((e) => e.exercise.libraryExerciseId);
    }));
    return Array.from(new Set(exerciseIds));
}
/**
 * Get every set in a workout
 */
export function getWorkoutExerciseSets(workout) {
    return workout.sections.flatMap((s) => s.entries.flatMap((entry) => {
        if (entry.__type === 'WorkoutExercise') {
            return entry.sets;
        }
        return entry.exercises.flatMap((e) => e.exercise.sets);
    }));
}
/**
 * Checks if a WorkoutAlternateGroup entry should be skipped because it overlaps with a WorkoutSuperset.
 * This prevents rendering the same exercise twice when it's both in an alternate group and a superset.
 */
export const shouldSkipEntry = (entry, sectionEntries) => {
    if (entry.__type === 'WorkoutAlternateGroup') {
        const overlappingExercise = sectionEntries.find((e) => e.__type === 'WorkoutSuperset' &&
            e.exercises.some(({ exercise }) => entry.exercises.some((e) => e.workoutExerciseId === exercise.workoutExerciseId)));
        return !!overlappingExercise;
    }
    return false;
};
/**
 * Gets exercises from an entry, handling the complex logic of supersets with alternate groups.
 *
 * For WorkoutSupersets: checks if any exercise in the superset is part of an alternate group,
 * and if so, uses the selected exercise from that alternate group.
 *
 * For WorkoutAlternateGroups: returns the selected exercise.
 *
 * For WorkoutExercise: returns the exercise as-is.
 *
 * @param entry - The entry to get exercises from
 * @param section - The section containing the entry (needed to check for alternate groups in supersets)
 * @param actualLayer - The actual layer to determine selected exercises in alternate groups
 */
export const getExercisesFromEntryWithSection = (entry, section, actualLayer) => {
    const isSuperset = entry.__type === 'WorkoutSuperset';
    const isAlternateGroup = entry.__type === 'WorkoutAlternateGroup';
    if (isSuperset) {
        // For supersets, check if any exercise is in an alternate group
        // and use the selected exercise from the alternate group.
        // Note: entry.exercises is already sorted by superset exercise sortOrder
        // from merge-workout-layers, so we use .map() to preserve that order
        // instead of re-sorting by exercise.sortOrder (which is the section position).
        const exercises = entry.exercises.map(({ exercise }) => {
            const alternateGroup = section.entries.find((e) => e.__type === 'WorkoutAlternateGroup' &&
                e.exercises.some((alt) => alt.workoutExerciseId === exercise.workoutExerciseId));
            if (alternateGroup) {
                const selectedExerciseId = getSelectedExerciseId(alternateGroup, actualLayer);
                const selected = alternateGroup.exercises.find((alt) => alt.workoutExerciseId === selectedExerciseId);
                return selected ? selected.exercise : exercise;
            }
            return exercise;
        });
        return exercises;
    }
    if (isAlternateGroup) {
        // For alternate groups, get the selected exercise
        const selectedExerciseId = getSelectedExerciseId(entry, actualLayer);
        const selectedExercise = entry.exercises.find((e) => e.workoutExerciseId === selectedExerciseId)?.exercise;
        return selectedExercise ? [selectedExercise] : [];
    }
    // Regular exercise
    return [entry];
};
//# sourceMappingURL=get-workout-exercises.js.map